import { useQuery } from "@tanstack/react-query";
import { organizationSettingsService } from "@/services/organizationSettings";

/** Shared with the Settings page so both read/write the same cache entry. */
export const ORGANIZATION_SETTINGS_QUERY_KEY = ["organizationSettings"] as const;

export const FALLBACK_DEFAULT_COMMISSION_PCT = 10;

export function useOrganizationSettingsQuery(enabled = true) {
  return useQuery({
    queryKey: ORGANIZATION_SETTINGS_QUERY_KEY,
    queryFn: () => organizationSettingsService.get(),
    enabled,
    staleTime: 60_000,
  });
}

function parseCommissionPct(raw: string | number | null | undefined): number {
  if (raw == null || raw === "") return FALLBACK_DEFAULT_COMMISSION_PCT;
  const n = Number(raw);
  return Number.isFinite(n) ? n : FALLBACK_DEFAULT_COMMISSION_PCT;
}

/**
 * The org's default commission / origination percentage for NEW contracts.
 * Falls back to 10 while loading (or if the org has no value) so callers
 * always have a number to compute with.
 */
export function useDefaultCommissionPct(): number {
  const { data } = useOrganizationSettingsQuery();
  return parseCommissionPct(data?.default_commission_percentage);
}

/** Org default commission for NEW refinance offers (falls back to 10). */
export function useDefaultRefiCommissionPct(): number {
  const { data } = useOrganizationSettingsQuery();
  return parseCommissionPct(data?.default_refi_commission_percentage);
}

/** When true (default), offer-calc Term is always days. */
export function useOfferTermAlwaysDays(): boolean {
  const { data } = useOrganizationSettingsQuery();
  return data?.offer_term_always_days ?? true;
}
